import { defineStore } from "pinia";
import { cloneDeep } from "lodash";

import { useAuthStore, useUIStore, useCrudStore, useDataStore } from "@/stores";
import { MONTHS, CHART_OPTIONS } from "@/constants";
import { ACCOUNT_TYPE_ORDER, ACCOUNT_TYPE_LABELS } from "@/constants/accountTypes";
import { PORTFOLIO_TERMS } from "@/constants/portfolioTerms";
import { portfolioDataSource } from "@/constants/samplePortfolioData";
import {
  commaSeparateThousands,
  getLastFour,
  parseLocalISODate,
  toLocalISODate,
} from "@/utilities";

// Investment statuses that are no longer part of the active portfolio.
const CLOSED_INVESTMENT_STATUSES = ["Bonds Matured", "Canceled", "Early Redemption"];

export const useInvestmentStore = defineStore("investmentStore", {
  state: () => ({
    // Dashboard Charts
    runningTotalInvestments: [],
    runningTotalDistributions: [],
    investmentsByOffering: [],
    chartData: { labels: [], values: [] },
    chartOptions: CHART_OPTIONS,
    percentageGrowth: 0,
    PNCAcctsExpiring: [],

    // Earnings for Info Nuggets
    metric_current_earnings: 0,

    hasNoDistributions: false,
    hasNoCompletedDistributions: false,
  }),
  actions: {
    // Completed distributions rolled up by payment date, newest first — the
    // shape both earnings tables render:
    //   { distribution_date, amount, simple_amount, compounding_amount }
    // `amount` is the interest earned that day; `simple_amount` is the slice
    // paid out in cash and `compounding_amount` the slice added to the bond.
    //
    // filter: "all" | "simple" (monthly payments) | "compounded" (accrued).
    // includeClosedInvestments: the dashboard's "Your returns" summary covers
    // the active portfolio only, while Portfolio > Earnings is the investor's
    // full history and keeps earnings from bonds that have since matured.
    getEarningsRows(filter = "all", { includeClosedInvestments = false } = {}) {
      const { investments, distributions } = this._portfolioData();

      const closedIds = new Set(
        investments
          .filter((inv) => CLOSED_INVESTMENT_STATUSES.includes(inv.status))
          .map((inv) => String(inv.record_id))
      );

      const grouped = distributions
        .filter((dist) => dist.is_completed)
        .filter((dist) => includeClosedInvestments || !closedIds.has(String(dist.investment_id)))
        .filter((dist) => this._matchesEarningType(dist, filter))
        .reduce((acc, dist) => {
          const dateKey = dist.distribution_date;

          if (!acc[dateKey]) {
            acc[dateKey] = {
              distribution_date: dateKey,
              amount: 0,
              simple_amount: 0,
              compounding_amount: 0,
              is_completed: true,
            };
          }

          const amount = this._interestPortion(dist, investments);
          acc[dateKey].amount += amount;
          if (dist.is_compounded) {
            acc[dateKey].compounding_amount += amount;
          } else {
            acc[dateKey].simple_amount += amount;
          }

          return acc;
        }, {});

      return Object.values(grouped).sort((a, b) =>
        String(b.distribution_date).localeCompare(String(a.distribution_date))
      );
    },
    async getInvestorEarnings() {
      try {
        return this.getEarningsRows("all", { includeClosedInvestments: false });
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    getDistributionsByMonth(investmentId = null, filter = null) {
      const authStore = useAuthStore();
      const dataStore = useDataStore();
      const crudStore = useCrudStore();

      let distributions = [];
      let closed_investments = [];

      if (investmentId) {
        distributions = dataStore.distributions?.filter(
          (dist) => dist.investment_id.toString() === investmentId.toString()
        );
      } else {
        // we're doing aggregations across investments
        let matured_investments = dataStore.investments
          .filter(
            (inv) =>
              inv.status === "Bonds Matured" ||
              inv.status === "Canceled" ||
              inv.status === "Early Redemption"
          )
          .map((inv) => inv.record_id);

        // mirroring how we filter investments for statements
        closed_investments = dataStore.investments
          .filter((inv) => {
            const now = new Date();
            const tenthOfTheMonth = new Date(now.getFullYear(), now.getMonth(), 10);
            let statementDate = new Date();

            if (now.getDate() >= 10) {
              statementDate = new Date(now.getFullYear(), now.getMonth(), 10);
            } else {
              statementDate = new Date(now.getFullYear(), now.getMonth() - 1, 10);
            }

            return (
              (inv.status === "Settled" && new Date(inv.first_payment_date) <= now) ||
              (inv.status === "Bonds Matured" && new Date(inv.maturity_date) > tenthOfTheMonth) ||
              (inv.status === "Early Redemption" &&
                new Date(inv.last_payment_date) >= statementDate &&
                new Date(inv.first_payment_date) <= statementDate)
            );
          })
          .map((inv) => inv.record_id);

        distributions =
          dataStore.distributions?.filter(
            (dist) => !matured_investments.includes(dist.investment_id)
          ) || [];
      }

      if (filter) {
        distributions = distributions.filter((dist) => {
          if (filter === "compounded") return dist.is_compounded;
          if (filter === "simple") return !dist.is_compounded;
          return dist;
        });
      }

      // Check for any distributions
      if (distributions.length === 0) {
        this.hasNoDistributions = true;
      } else {
        this.hasNoDistributions = false;
      }

      // Check for completed distributions
      if (distributions?.filter((dist) => dist.is_completed).length === 0) {
        this.hasNoCompletedDistributions = true;
      } else {
        this.hasNoCompletedDistributions = false;
      }

      if (!this.hasNoCompletedDistributions && !investmentId) {
        // we have completed distributions, mirror what we do for statements
        distributions = distributions.filter(
          (dist) =>
            closed_investments.includes(dist.investment_id) &&
            (dist.is_completed || dist.is_title_transfer) &&
            dist.distribution_date <= new Date().toISOString().split("T")[0]
        );
      } else if (!this.hasNoCompletedDistributions && investmentId) {
        distributions = distributions.filter(
          (dist) =>
            (dist.is_completed || dist.is_title_transfer) &&
            dist.distribution_date <= new Date().toISOString().split("T")[0]
        );
      }

      let grouped = {};
      for (let dist of distributions) {
        let date = new Date(dist.distribution_date);
        let year = date.getFullYear();
        let month = date.getMonth() + 1; // getMonth is 0-indexed

        let key = `${year}-${month}`;
        if (!grouped[key]) {
          grouped[key] = {
            year,
            month,
            totalAmount: 0,
            is_compounded: dist.is_compounded ? true : false,
            investments: [],
          };
        }
        let investment = dataStore.investments.find(
          (inv) => inv.record_id.toString() === dist.investment_id.toString()
        );

        // subtract maturity out of distribution on maturity date
        if (dist.amount >= investment?.amount) {
          grouped[key].totalAmount += dist.amount - investment?.amount || 0;
        } else {
          grouped[key].totalAmount += dist.amount || 0;
        }
        grouped[key].investments.push(dist.investment_id);
      }

      // Step 3: Convert to array and sort
      let result = Object.values(grouped).sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });

      return result;
    },
    getDashboardChartData(investmentId = null, filter = null) {
      const crudStore = useCrudStore();
      const UIStore = useUIStore();
      const authStore = useAuthStore();
      const dataStore = useDataStore();

      try {
        this.runningTotalDistributions = [];
        UIStore.is_chart_loading = true;

        let distributionsByMonth = this.getDistributionsByMonth(investmentId, filter);

        let compoundedAmount = 0;
        let runningNonCompoundedAmount = 0;
        let maturedAmount = 0;
        let totalInvestmentAmount = 0;
        distributionsByMonth.forEach((distribution, index) => {
          compoundedAmount = distribution.is_compounded
            ? distribution.totalAmount + compoundedAmount
            : compoundedAmount;
          runningNonCompoundedAmount = distribution.is_compounded
            ? runningNonCompoundedAmount
            : distribution.totalAmount + runningNonCompoundedAmount;
          // separate out principal amount
          let investments_in_distribution = dataStore.investments.filter((investment) =>
            distribution.investments.includes(investment.record_id)
          );
          maturedAmount = 0;
          totalInvestmentAmount = 0;
          for (let investment of investments_in_distribution) {
            let maturity_date = new Date(investment.maturity_date);
            let month = maturity_date.getMonth() + 1; // Months are 0-based, so add 1
            let year = maturity_date.getFullYear();
            maturedAmount = investment.amount;
            totalInvestmentAmount += investment.amount;
            // if (
            //   month + "" + year == distribution.month + "" + distribution.year &&
            //   investment.status == "Bonds Matured"
            // ) {
            //   if (distribution.is_compounded) {
            //     compoundedAmount -= maturedAmount;
            //   } else {
            //     runningNonCompoundedAmount -= maturedAmount;
            //   }
            // }
          }
          this.runningTotalDistributions.push({
            ...distribution,
            ...{
              compoundedAmount: compoundedAmount,
              runningNonCompoundedAmount: runningNonCompoundedAmount,
              totalInvestmentAmount: totalInvestmentAmount,
            },
          });
          this.runningTotalInvestments.push({
            ...distribution,
            ...{
              compoundedAmount: compoundedAmount,
              runningNonCompoundedAmount: runningNonCompoundedAmount,
            },
          });
        });
        UIStore.is_chart_loading = false;

        return this.runningTotalDistributions;
      } catch (err) {
        console.error(err);
      }
    },
    calculateDashboardInvestorMetrics(investmentId = null, filter = null) {
      const crudStore = useCrudStore();
      const authStore = useAuthStore();
      const dataStore = useDataStore();

      let metrics = [];

      if (this.hasNoDistributions) {
        return [];
      }

      let distributions = dataStore.distributions;
      let investments = dataStore.investments;

      let matured_investments = dataStore.investments
        .filter(
          (inv) =>
            inv.status === "Bonds Matured" ||
            inv.status === "Canceled" ||
            inv.status === "Early Redemption"
        )
        .map((inv) => inv.record_id);

      distributions =
        dataStore.distributions?.filter(
          (dist) => !matured_investments.includes(dist.investment_id)
        ) || [];

      if (filter) {
        distributions = distributions.filter((dist) => {
          if (filter === "compounded") return dist.is_compounded;
          if (filter === "simple") return !dist.is_compounded;
          return dist;
        });

        investments = investments.filter((inv) => {
          if (filter === "compounded") return inv.is_compounded;
          if (filter === "simple") return !inv.is_compounded;
          return inv;
        });
      }

      let latestDistribution =
        this.runningTotalDistributions[this.runningTotalDistributions.length - 1];
      let initialDistribution = this.runningTotalDistributions[1];

      this.percentageGrowth =
        this.hasNoCompletedDistributions || this.hasNoDistributions
          ? 0
          : ((latestDistribution.totalInvestmentAmount +
              latestDistribution.runningNonCompoundedAmount +
              latestDistribution.compoundedAmount -
              (latestDistribution.totalInvestmentAmount +
                initialDistribution.runningNonCompoundedAmount +
                initialDistribution.compoundedAmount)) /
              (latestDistribution.totalInvestmentAmount +
                initialDistribution.runningNonCompoundedAmount +
                initialDistribution.compoundedAmount ==
              0
                ? 1
                : latestDistribution.totalInvestmentAmount +
                  initialDistribution.runningNonCompoundedAmount +
                  initialDistribution.compoundedAmount)) *
            100;

      /**** Next Payment Metric ****/
      const today = new Date();
      const next10th = new Date(today.getFullYear(), today.getMonth(), 10);

      // If the 10th has already passed this month, move to next month
      if (today.getDate() >= 10) {
        next10th.setMonth(next10th.getMonth() + 1);
      }

      let nextPaymentAmount = 0;

      if (investmentId) {
        nextPaymentAmount = distributions
          .filter(
            (distribution) =>
              distribution.distribution_date === next10th.toISOString().split("T")[0] &&
              distribution.investment_id.toString() === investmentId.toString()
          )
          .reduce((sum, distribution) => {
            const investment = dataStore.investments.find(
              (inv) => inv.record_id === distribution.investment_id
            );
            const distAmount = Number(distribution.amount) || 0;
            const invAmount = Number(investment?.amount) || 0;

            const adjustedAmount = distAmount > invAmount ? distAmount - invAmount : distAmount;

            return sum + adjustedAmount;
          }, 0);
      } else {
        nextPaymentAmount = distributions
          .filter(
            (distribution) =>
              distribution.distribution_date === next10th.toISOString().split("T")[0]
          )
          .reduce((sum, distribution) => {
            const investment = dataStore.investments.find(
              (inv) => inv.record_id === distribution.investment_id
            );

            if (!["Settled"].includes(investment?.status)) return sum;

            const distAmount = Number(distribution.amount) || 0;
            const invAmount = Number(investment?.amount) || 0;

            const adjustedAmount = distAmount > invAmount ? distAmount - invAmount : distAmount;

            return sum + adjustedAmount;
          }, 0);
      }

      metrics.push({
        label: "Upcoming Return",
        value: this.formatCurrency(nextPaymentAmount.toFixed(2)),
        subtext: nextPaymentAmount
          ? "Due on " +
            next10th.toLocaleDateString("en-US", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "",
        subtextIcon: "",
      });

      /**** Current Earnings Metric ****/
      let currentEarnings =
        this.hasNoCompletedDistributions || this.hasNoDistributions
          ? 0
          : latestDistribution.compoundedAmount + latestDistribution.runningNonCompoundedAmount;

      metrics.push({
        label: "Current Returns",
        value: this.formatCurrency(currentEarnings),
        subtext: "",
        // this.hasNoCompletedDistributions || this.hasNoDistributions
        //   ? "No payments completed yet"
        //   : "Up " + commaSeparateThousands(this.percentageGrowth.toFixed(2)) + "%",
        subtextIcon: "",
      });

      this.metric_current_earnings = currentEarnings;

      /**** Current Bond Value Metric ****/
      let currentBondValue = 0;
      if (investmentId) {
        currentBondValue = investments
          .filter(
            (inv) =>
              inv.status !== "Canceled" && inv.record_id.toString() === investmentId.toString()
          )
          .reduce((sum, inv) => sum + (Number(inv.current_nav) || 0), 0);
      } else {
        currentBondValue = investments
          .filter((inv) => inv.status === "Settled")
          .reduce((sum, inv) => sum + (Number(inv.current_nav) || 0), 0);
      }

      // Principle of all investments from earliest first_payment_date
      let earliestPaymentDate = null;
      if (investmentId) {
        earliestPaymentDate = investments
          .filter(
            (inv) =>
              inv.status !== "Canceled" &&
              inv.first_payment_date &&
              inv.record_id.toString() === investmentId.toString()
          )
          .reduce((earliest, inv) => {
            const d = new Date(inv.first_payment_date);
            return d <= earliest ? d : earliest;
          }, new Date(investments[0].first_payment_date));
      } else {
        earliestPaymentDate = investments
          .filter((inv) => inv.status !== "Canceled" && inv.first_payment_date)
          .reduce((earliest, inv) => {
            const d = new Date(inv.first_payment_date);
            return d <= earliest ? d : earliest;
          }, new Date(investments[0].first_payment_date));
      }

      const earliestDateStr = earliestPaymentDate.toISOString().split("T")[0];

      // Sum amounts matching that earliest date
      let bondValueAtInception = 0;
      if (investmentId) {
        bondValueAtInception = investments
          .filter(
            (inv) =>
              inv.first_payment_date?.split("T")[0] === earliestDateStr &&
              inv.record_id.toString() === investmentId.toString()
          )
          .reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);
      } else {
        bondValueAtInception = investments
          .filter((inv) => inv.first_payment_date?.split("T")[0] === earliestDateStr)
          .reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);
      }

      metrics.push({
        label: "Current Bond Value",
        value: this.formatCurrency(currentBondValue.toFixed(2)),
        subtext: "",
        // this.hasNoCompletedDistributions || this.hasNoDistributions
        //   ? "No payments completed yet"
        //   : "Up " +
        //     commaSeparateThousands(
        //       (((currentBondValue - bondValueAtInception) / bondValueAtInception) * 100).toFixed(
        //         2
        //       )
        //     ) +
        //     "%",
        subtextIcon: "",
      });

      return metrics;
    },
    // ─────────────────────────────────────────────────────────────────────
    // Active portfolio (PHX-151 dashboard rework): Principal + Accrued
    // interest, broken out by account type (Personal/Joint/IRA/Entity).
    //
    // This is intentionally separate from calculateDashboardInvestorMetrics()
    // above, which still drives the legacy Investments/InvestmentDetails
    // charts (percentageGrowth, hasNoDistributions, etc.) — those pages
    // aren't part of this rework yet, so their data source is left alone.
    // ─────────────────────────────────────────────────────────────────────
    // The one place these surfaces read records from. Returns live data from
    // the data store, or the design fixture when the sample switch in
    // constants/samplePortfolioData.js is on — either way the same maths runs
    // on top, so sample mode can't hide a broken calculation.
    _portfolioData() {
      return portfolioDataSource(useDataStore());
    },
    _getActiveInvestments() {
      return this._portfolioData().investments.filter((inv) => inv.status === "Settled");
    },
    // The payment-type pills in Portfolio > Earnings ("All" / "Monthly
    // payments" / "Accrued interest") narrow every figure on the tab, so the
    // same two predicates are used on both sides of the data: a distribution is
    // matched by how it was paid, an investment by how it is set to pay.
    //
    // filter: "all" | "simple" (paid out monthly) | "compounded" (accrued).
    _matchesEarningType(distribution, filter) {
      if (filter === "simple") return !distribution.is_compounded;
      if (filter === "compounded") return Boolean(distribution.is_compounded);
      return true;
    },
    _matchesPaymentType(investment, filter) {
      if (filter === "simple") return !investment.is_compounded;
      if (filter === "compounded") return Boolean(investment.is_compounded);
      return true;
    },
    // `type` casing isn't reliable across every investment origin (see
    // InvestmentSuccess.vue's own toLowerCase() guard), so this lowercases
    // both sides before comparing — a title-cased check here missed
    // differently-cased "IRA" values and silently fell through to
    // "Personal" instead (PHX-312, mirrored fix from Admin-Portal-V2's
    // useInvestorViewStore.js).
    _classifyAccountType(type) {
      const normalizedType = type?.toLowerCase() ?? "";
      const IRA_KEYWORDS = ["ira", "solo", "hsa"];
      if (IRA_KEYWORDS.some((keyword) => normalizedType.includes(keyword))) return "IRA";
      if (normalizedType === "joint") return "Joint";
      if (normalizedType === "entity") return "Entity";
      return "Personal";
    },
    // Which side of the "IRA" bucket an investment belongs to, for the card
    // label — the bucket also absorbs HSA (and Solo) accounts, so the label
    // shouldn't always just say "IRA" over what might be an HSA balance
    // (PHX-312).
    _iraGroupTag(type) {
      return type?.toLowerCase().includes("hsa") ? "HSA" : "IRA";
    },
    // The earning inside a distribution. On a maturity date the payment
    // returns the principal alongside the last interest payment; only the
    // interest is an earning. record_ids are compared as strings because
    // distributions and investments don't always agree on the type.
    _interestPortion(distribution, investments) {
      const investment = investments.find(
        (inv) => String(inv.record_id) === String(distribution.investment_id)
      );
      const distAmount = Number(distribution.amount) || 0;
      const invAmount = Number(investment?.amount) || 0;
      return distAmount > invAmount ? distAmount - invAmount : distAmount;
    },
    // What the investor actually put in. Usually `amount`, but discounted
    // (OID) bonds are bought below face value — `amount` holds the face/par
    // value that matures out, not the price paid, so using it as principal
    // silently folds the not-yet-accreted discount into "principal" instead
    // of "accrued interest" (PHX-312).
    _investedPrincipal(investment) {
      if (investment.is_discounted_bond) {
        return Number(investment.discounted_bond_amount) || 0;
      }
      return Number(investment.amount) || 0;
    },
    // Interest compounded back into the bond and still held. Bonds that pay
    // out monthly hold none — their interest has already been distributed and
    // is counted under monthly payments instead.
    _accruedInterest(investment) {
      if (!investment.is_compounded) return 0;
      const nav = Number(investment.current_nav) || 0;
      const principal = this._investedPrincipal(investment);
      return Math.max(nav - principal, 0);
    },
    // Lifetime cash paid out (non-compounded, already completed) for a set of
    // investments.
    _sumMonthlyPayments(investmentIds) {
      const { investments, distributions } = this._portfolioData();
      const ids = new Set(investmentIds.map(String));
      return distributions
        .filter(
          (dist) => dist.is_completed && !dist.is_compounded && ids.has(String(dist.investment_id))
        )
        .reduce((sum, dist) => sum + this._interestPortion(dist, investments), 0);
    },
    _earliestActiveInvestmentDate() {
      return this._getActiveInvestments()
        .filter((inv) => inv.first_payment_date)
        .reduce((earliest, inv) => {
          const date = parseLocalISODate(inv.first_payment_date);
          return !earliest || date < earliest ? date : earliest;
        }, null);
    },
    // The investor's next earning: the earliest distribution dated today or
    // later that hasn't been paid yet, across their settled investments. This
    // reads the schedule rather than assuming the 10th, so it stays correct
    // for off-cycle payment dates and for investors whose next payment is
    // more than a month out.
    //
    // Returns the date plus one row per contributing investment. The hero KPI,
    // the Portfolio stat card and the breakdown modal all render from this one
    // result, so their totals can never disagree.
    _buildUpcomingEarnings() {
      const { investments, distributions, bank_accounts } = this._portfolioData();
      const settled = new Map(
        investments
          .filter((inv) => inv.status === "Settled")
          .map((inv) => [String(inv.record_id), inv])
      );
      const todayStr = toLocalISODate(new Date());

      const scheduled = distributions
        .filter((dist) => !dist.is_completed && settled.has(String(dist.investment_id)))
        .map((dist) => ({ dist, date: String(dist.distribution_date ?? "").split("T")[0] }))
        .filter((entry) => entry.date >= todayStr);

      if (!scheduled.length) {
        return { date: null, rows: [], totalPayment: 0, totalAccrued: 0 };
      }

      const nextDate = scheduled.reduce(
        (earliest, entry) => (entry.date < earliest ? entry.date : earliest),
        scheduled[0].date
      );

      const rows = this._buildEarningsRows(
        scheduled.filter((entry) => entry.date === nextDate).map((entry) => entry.dist),
        investments,
        bank_accounts
      );

      return { date: parseLocalISODate(nextDate), rows, ...this._totalEarnings(rows) };
    },
    // Every investment that paid on one specific date — the historical
    // counterpart to _buildUpcomingEarnings(), behind each row of the earnings
    // history in Portfolio > Earnings. Investments that have since matured or
    // been redeemed are included: the investor was still paid that month.
    //
    // `filter` mirrors the pill that was active on the row the investor
    // clicked, so the breakdown totals always reconcile with the row above it.
    _buildPaidEarningsForDate(distributionDate, filter = "all") {
      const { investments, distributions, bank_accounts } = this._portfolioData();
      const target = String(distributionDate ?? "").split("T")[0];
      if (!target) return { date: null, rows: [], totalPayment: 0, totalAccrued: 0 };

      const paid = distributions
        .filter((dist) => dist.is_completed)
        .filter((dist) => String(dist.distribution_date ?? "").split("T")[0] === target)
        .filter((dist) => this._matchesEarningType(dist, filter));

      const rows = this._buildEarningsRows(paid, investments, bank_accounts);

      return {
        date: rows.length ? parseLocalISODate(target) : null,
        rows,
        ...this._totalEarnings(rows),
      };
    },
    // One breakdown row per distribution: exactly one of a monthly payment
    // (with the account it lands in) or interest compounded into the bond.
    _buildEarningsRows(distributions, investments, bankAccounts) {
      return distributions.map((dist) => {
        const investment = investments.find(
          (inv) => String(inv.record_id) === String(dist.investment_id)
        );
        const amount = this._interestPortion(dist, investments);
        const isCompounded = dist.is_compounded ?? investment?.is_compounded;
        return {
          investmentId: investment?.record_id ?? dist.investment_id,
          type: investment?.type ?? null,
          paymentSentTo:
            isCompounded || !investment
              ? null
              : this._distributionDestination(investment, bankAccounts),
          monthlyPayment: isCompounded ? null : amount,
          accruedInterest: isCompounded ? amount : null,
        };
      });
    },
    _totalEarnings(rows) {
      return {
        totalPayment: rows.reduce((sum, row) => sum + (row.monthlyPayment || 0), 0),
        totalAccrued: rows.reduce((sum, row) => sum + (row.accruedInterest || 0), 0),
      };
    },
    // Where a monthly payment lands: the masked linked bank for ACH, otherwise
    // the method itself (Check / Wire). Null when it can't be resolved —
    // callers render "-".
    _distributionDestination(investment, bankAccounts) {
      const method = investment.distribution_method;
      if (method && method !== "ACH") return method;

      const bank = bankAccounts.find(
        (account) => String(account.record_id) === String(investment.distribution_account)
      );
      if (!bank) return null;
      return `${bank.financial_institution_name} ****${getLastFour(String(bank.account_number ?? ""))}`;
    },
    // One pass over active investments, grouped by account type, in
    // display order. Only account types the investor actually holds are
    // returned — callers never need an "is this empty" check.
    //
    // `filter` narrows the pass to one payment type for the Portfolio >
    // Earnings pills; the dashboard hero and the page-level totals leave it at
    // "all" and see the whole portfolio.
    _buildActivePortfolioGroups(filter = "all") {
      const groups = {};

      for (const investment of this._getActiveInvestments()) {
        if (!this._matchesPaymentType(investment, filter)) continue;
        const accountType = this._classifyAccountType(investment.type);
        if (!groups[accountType]) {
          groups[accountType] = {
            accountType,
            investments: [],
            principal: 0,
            accruedInterest: 0,
            iraTags: new Set(),
          };
        }
        const group = groups[accountType];
        group.investments.push(investment);
        group.principal += this._investedPrincipal(investment);
        group.accruedInterest += this._accruedInterest(investment);
        if (accountType === "IRA") group.iraTags.add(this._iraGroupTag(investment.type));
      }

      for (const group of Object.values(groups)) {
        group.monthlyPayments = this._sumMonthlyPayments(
          group.investments.map((inv) => inv.record_id)
        );
      }

      return ACCOUNT_TYPE_ORDER.map((accountType) => groups[accountType]).filter(Boolean);
    },
    // Top-line KPI values for the hero, as ready-to-render display strings.
    // The KPI cards themselves are hard-coded in Dashboard.vue (they're each a
    // different shape); this just feeds them their numbers. Account-type cards
    // are the data-driven / looped part — see getPortfolioAccountCards().
    getPortfolioSummary() {
      const groups = this._buildActivePortfolioGroups();
      const totalPrincipal = groups.reduce((sum, g) => sum + g.principal, 0);
      const totalAccrued = groups.reduce((sum, g) => sum + g.accruedInterest, 0);
      const totalMonthlyPayments = groups.reduce((sum, g) => sum + g.monthlyPayments, 0);

      const earliestDate = this._earliestActiveInvestmentDate();
      const sinceLabel = earliestDate
        ? earliestDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })
        : "";

      const upcoming = this._buildUpcomingEarnings();

      return {
        principal: this.formatCurrency(totalPrincipal),
        principalSince: sinceLabel ? `Since ${sinceLabel}` : "",
        accruedInterest: this.formatCurrency(totalAccrued),
        monthlyPayments: this.formatCurrency(totalMonthlyPayments),
        nextEarnings: this.formatCurrency(upcoming.totalPayment + upcoming.totalAccrued),
        // Empty when nothing is scheduled yet (e.g. an investment that has
        // settled but hasn't reached its first payment date).
        nextEarningsOn: upcoming.date ? `On ${this._formatDayOrdinalMonth(upcoming.date)}` : "",
        // Same date, bare: reads inside the tile label as "Next monthly payment
        // (June 24)", where the sentence form above would not fit.
        nextEarningsDate: upcoming.date ? this._formatMonthDay(upcoming.date) : "",
        hasUpcomingEarnings: Boolean(upcoming.date),
      };
    },
    // The IRA bucket also holds HSA (and Solo) accounts under one card, so
    // its label reflects what this investor actually holds — "HSA
    // investments" for an HSA-only holder, "IRA/HSA investments" when they
    // hold both — rather than always reading "IRA investments" over what
    // might be entirely an HSA balance (PHX-312).
    _accountTypeLabel(group) {
      if (group.accountType !== "IRA" || !group.iraTags?.size) {
        return ACCOUNT_TYPE_LABELS[group.accountType];
      }
      const tagOrder = ["IRA", "HSA"];
      const heldTags = tagOrder.filter((tag) => group.iraTags.has(tag));
      return `${heldTags.join("/")} investments`;
    },
    // Returns one descriptor per held account type. "Monthly payments"
    // subLine is only included when that group actually has paid-monthly
    // (non-compounded) investments.
    //
    // Under a payment-type pill only the matching earning can be non-zero —
    // bonds that pay monthly accrue nothing, compounding bonds pay out nothing
    // — so the other subLine is dropped rather than shown as $0.
    getPortfolioAccountCards(filter = "all") {
      return this._buildActivePortfolioGroups(filter).map((group) => {
        const subLines = [{ label: "Principal", value: this.formatCurrency(group.principal) }];
        if (filter !== "simple") {
          subLines.push({
            label: "Accrued interest",
            value: this.formatCurrency(group.accruedInterest),
          });
        }
        const hasMonthlyPayments = filter !== "compounded" && group.monthlyPayments > 0;
        if (hasMonthlyPayments) {
          subLines.push({
            label: "Monthly payments",
            value: this.formatCurrency(group.monthlyPayments),
          });
        } else if (filter === "all") {
          // Unfiltered, every account card carries the same three rows so the
          // cards stay the same height side by side; types whose bonds all
          // compound read "Not applicable" rather than dropping the row.
          subLines.push({
            label: "Monthly payments",
            value: PORTFOLIO_TERMS.notApplicable,
          });
        }

        return {
          key: group.accountType,
          label: this._accountTypeLabel(group),
          labelSize: "sm",
          value: this.formatCurrency(group.principal + group.accruedInterest),
          subLines,
          hasMonthlyPayments,
        };
      });
    },
    // Total active portfolio value (principal + accrued interest), formatted
    // for the hero headline.
    getTotalActivePortfolioValue() {
      const groups = this._buildActivePortfolioGroups();
      const total = groups.reduce((sum, g) => sum + g.principal + g.accruedInterest, 0);
      return this.formatCurrency(total);
    },
    // The earnings-breakdown modal, for one payment date: one row per
    // investment, each exactly one of a monthly payment (with its destination)
    // or accrued interest (destination "-").
    //
    // With no date it describes the next scheduled payment — the same
    // _buildUpcomingEarnings() result the hero KPI and the Portfolio stat card
    // render, so those totals and this modal can never disagree. Pass a
    // distribution date (and the pill in force) to describe a payment the
    // investor has already received, which is what each row of the earnings
    // history opens.
    getEarningsBreakdown(distributionDate = null, filter = "all") {
      const isUpcoming = !distributionDate;
      const { date, rows, totalPayment, totalAccrued } = isUpcoming
        ? this._buildUpcomingEarnings()
        : this._buildPaidEarningsForDate(distributionDate, filter);

      return {
        isUpcoming,
        hasEarnings: Boolean(date),
        monthLabel: date
          ? date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
          : "",
        // Day only ("10th June") — the modal prefixes it with "On" or "Paid on"
        // depending on whether the payment is scheduled or already made.
        dayLabel: date ? this._formatDayOrdinalMonth(date) : "",
        totalEarning: this.formatCurrency(totalPayment + totalAccrued),
        monthlyPayment: this.formatCurrency(totalPayment),
        accruedInterest: this.formatCurrency(totalAccrued),
        rows: rows.map((row) => ({
          investmentId: row.investmentId,
          investmentIdShort: `...${String(row.investmentId).slice(-4)}`,
          paymentSentTo: row.paymentSentTo || "-",
          monthlyPayment:
            row.monthlyPayment != null ? this.formatCurrency(row.monthlyPayment) : "-",
          accruedInterest:
            row.accruedInterest != null ? this.formatCurrency(row.accruedInterest) : "-",
          type: row.type || "-",
        })),
      };
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value) || 0);
    },
    // Formats a date as "June 24" (full month + bare day).
    _formatMonthDay(date) {
      return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
    },
    // Formats a date as "24th June" (ordinal day + full month) to match the design.
    _formatDayOrdinalMonth(date) {
      const day = date.getDate();
      const j = day % 10;
      const k = day % 100;
      const suffix =
        j === 1 && k !== 11 ? "st" : j === 2 && k !== 12 ? "nd" : j === 3 && k !== 13 ? "rd" : "th";
      const month = date.toLocaleDateString("en-US", { month: "long" });
      return `${day}${suffix} ${month}`;
    },
  },
});
